import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Pencil, Users, QrCode, Trash2, Eye, EyeOff, Ticket, CheckCircle2, CalendarDays, ExternalLink, Copy, Banknote, TrendingUp, Download, Facebook, Link2, FileDown, Send, Share2, Check, Loader2, Sparkles, RefreshCw, Mail, Globe, Instagram, Stethoscope, XCircle,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, CartesianGrid } from "recharts";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { ReplyTrackingHealthCard } from "../components/ReplyTrackingHealthCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

const RowAction = ({ label, onClick, testId, danger = false, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className={`h-8 w-8 text-muted-foreground ${danger ? "hover:text-[hsl(var(--danger))]" : "hover:text-foreground"}`}
        onClick={onClick}
        aria-label={label}
        data-testid={testId}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="top" className="text-xs">{label}</TooltipContent>
  </Tooltip>
);
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { api, formatDateTime, errorMessage, errorTechnical, eventShareUrl, openFacebookShare } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

export default function DashboardPage() {
  const { organizer, refresh } = useAuth();
  const { t } = useLang();
  const [verifyCode, setVerifyCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const submitVerifyCode = async () => {
    if (verifyCode.trim().length < 4) {
      toast.error(t("Skriv inn koden fra e-posten"));
      return;
    }
    setVerifying(true);
    try {
      await api.post("/auth/verify-email", { code: verifyCode.trim() });
      await refresh();
      toast.success(t("E-postadressen er bekreftet!"));
      setVerifyCode("");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  const resendVerifyCode = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success(t("Ny kode er sendt til e-postadressen din"));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [sales, setSales] = useState(null);
  const [salesPeriod, setSalesPeriod] = useState("week");
  const [chartReady, setChartReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [posterTarget, setPosterTarget] = useState(null);
  const [posterStyle, setPosterStyle] = useState("klassisk");
  // Format: A4/A3 = PDF-plakat, square/story/landscape = sosiale bilder (JPG)
  const [posterFormat, setPosterFormat] = useState("A4");
  const posterIsPdf = posterFormat === "A4" || posterFormat === "A3";
  const [posterDownloading, setPosterDownloading] = useState(false);
  const [posterPreview, setPosterPreview] = useState(null);
  const [posterPreviewLoading, setPosterPreviewLoading] = useState(false);
  // AI-design: status (tilgang, kvote, aktivt forslag) + generering
  const [posterAi, setPosterAi] = useState(null);
  const [posterAiLoading, setPosterAiLoading] = useState(false);
  const [posterAiGenerating, setPosterAiGenerating] = useState(false);
  const [posterAiTest, setPosterAiTest] = useState(null); // resultat frå «Test AI-tjenesten»
  const [posterAiTesting, setPosterAiTesting] = useState(false);
  const [posterNonce, setPosterNonce] = useState(0); // tvinger ny forhåndsvisning etter generering/bytte av forslag
  // Publisering: heimeside (Right in-bilde + innbyggingskode), Facebook, Instagram
  const [posterSocial, setPosterSocial] = useState(null);
  const [publishing, setPublishing] = useState(null); // "homepage" | "facebook" | "instagram" | "revert"
  const [confirmPublish, setConfirmPublish] = useState(null); // "facebook" | "instagram"
  const [igKind, setIgKind] = useState("post"); // Instagram: "post" | "story" | "both"
  const [publishWhen, setPublishWhen] = useState("now"); // "now" | "schedule"
  const [scheduleAt, setScheduleAt] = useState(""); // datetime-local (lokal tid)
  const [cancellingJob, setCancellingJob] = useState(null);
  const [embedOpen, setEmbedOpen] = useState(false);

  const loadPosterSocial = useCallback(async (eventId) => {
    try {
      const res = await api.get(`/my/events/${eventId}/social/status`);
      setPosterSocial(res.data);
    } catch {
      setPosterSocial(null);
    }
  }, []);

  const loadPosterAi = useCallback(async (eventId) => {
    setPosterAiLoading(true);
    try {
      const res = await api.get(`/my/events/${eventId}/poster-ai/status`);
      setPosterAi(res.data);
    } catch {
      setPosterAi(null);
    } finally {
      setPosterAiLoading(false);
    }
  }, []);

  useEffect(() => {
    setPosterAiTest(null);
    if (!posterTarget) { setPosterAi(null); setPosterSocial(null); return; }
    loadPosterAi(posterTarget.id);
    loadPosterSocial(posterTarget.id);
  }, [posterTarget, loadPosterAi, loadPosterSocial]);

  const generatePosterAi = async () => {
    if (!posterTarget) return;
    setPosterAiGenerating(true);
    try {
      const res = await api.post(`/my/events/${posterTarget.id}/poster-ai/generate`);
      toast.success(t("AI-forslaget er klart ({n} av {max} brukt)", { n: res.data.used, max: res.data.max }));
      await loadPosterAi(posterTarget.id);
      setPosterStyle("ai");
      setPosterNonce((n) => n + 1);
    } catch (err) {
      const technical = errorTechnical(err);
      toast.error(errorMessage(err), technical ? { description: technical, duration: 12000 } : undefined);
      await loadPosterAi(posterTarget.id);
    } finally {
      setPosterAiGenerating(false);
    }
  };

  const runPosterAiTest = async () => {
    setPosterAiTesting(true);
    try {
      const res = await api.post("/my/poster-ai/selftest");
      setPosterAiTest(res.data);
      if (res.data.ok) toast.success(t("AI-tjenesten svarer – nøkkel og tilkobling er i orden"));
      else toast.error(t("AI-testen fant et problem – se detaljene under knappen"));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setPosterAiTesting(false);
    }
  };

  const selectPosterArt = async (artId) => {
    if (!posterTarget) return;
    try {
      await api.post(`/my/events/${posterTarget.id}/poster-ai/use/${artId}`);
      await loadPosterAi(posterTarget.id);
      setPosterNonce((n) => n + 1);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  // Ekte forhåndsvisning av plakaten (rendret fra samme PDF som lastes ned)
  useEffect(() => {
    if (!posterTarget) {
      setPosterPreviewLoading(false);
      setPosterPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
      return;
    }
    if (posterStyle === "ai" && (!posterAi || !posterAi.art)) {
      // AI-design uten forslag ennå: ingen forhåndsvisning å hente (vises som tom tilstand med knapp)
      setPosterPreviewLoading(false);
      setPosterPreview((old) => { if (old) URL.revokeObjectURL(old); return null; });
      return;
    }
    let cancelled = false;
    (async () => {
      setPosterPreviewLoading(true);
      try {
        const res = posterIsPdf
          ? await api.get(`/my/events/${posterTarget.id}/poster-preview`, {
              params: { size: posterFormat, style: posterStyle, v: posterNonce },
              responseType: "blob",
            })
          : await api.get(`/my/events/${posterTarget.id}/social-image`, {
              params: { format: posterFormat, style: posterStyle, w: 600, v: posterNonce },
              responseType: "blob",
            });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        setPosterPreview((old) => {
          if (old) URL.revokeObjectURL(old);
          return url;
        });
      } catch {
        if (!cancelled) setPosterPreview(null);
      } finally {
        if (!cancelled) setPosterPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [posterTarget, posterStyle, posterFormat, posterNonce, posterAi?.art?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPoster = async () => {
    if (!posterTarget) return;
    setPosterDownloading(true);
    try {
      const res = posterIsPdf
        ? await api.get(`/my/events/${posterTarget.id}/poster`, { params: { size: posterFormat, style: posterStyle }, responseType: "blob" })
        : await api.get(`/my/events/${posterTarget.id}/social-image`, { params: { format: posterFormat, style: posterStyle, download: true }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = posterIsPdf ? `plakat-${posterFormat.toLowerCase()}-${posterStyle}.pdf` : `rightin-${posterFormat}-${posterStyle}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(posterIsPdf ? t("Plakaten er lastet ned – klar for print") : t("Bildet er lastet ned – klart til deling"));
      if (posterIsPdf) setPosterTarget(null);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setPosterDownloading(false);
    }
  };

  const publishHomepage = async () => {
    if (!posterTarget) return;
    setPublishing("homepage");
    try {
      await api.post(`/my/events/${posterTarget.id}/social/homepage`, { style: posterStyle });
      toast.success(t("Plakatbildet er nå arrangementsbildet på Right in – innbyggingskoden er klar"));
      await loadPosterSocial(posterTarget.id);
      fetchEvents();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setPublishing(null);
    }
  };

  const revertHomepage = async () => {
    if (!posterTarget) return;
    setPublishing("revert");
    try {
      await api.delete(`/my/events/${posterTarget.id}/social/homepage`);
      toast.success(t("Arrangementet bruker igjen ditt eget bilde"));
      await loadPosterSocial(posterTarget.id);
      fetchEvents();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setPublishing(null);
    }
  };

  const openConfirm = (platform) => {
    setIgKind("post");
    setPublishWhen("now");
    // Forslag: i morgen kl. 18:00 lokal tid
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(18, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    setScheduleAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    setConfirmPublish(platform);
  };

  const publishSocial = async (platform) => {
    if (!posterTarget) return;
    const body = { style: posterStyle, kind: platform === "instagram" ? igKind : "post" };
    if (publishWhen === "schedule") {
      if (!scheduleAt) {
        toast.error(t("Velg dato og klokkeslett for publiseringen"));
        return;
      }
      body.scheduled_at = new Date(scheduleAt).toISOString(); // lokal tid -> UTC
    }
    setConfirmPublish(null);
    setPublishing(platform);
    try {
      const { data } = await api.post(`/my/events/${posterTarget.id}/social/${platform}`, body);
      if (data.status === "scheduled") {
        toast.success(t("Publiseringen er planlagt til {date}", { date: formatDateTime(data.job?.scheduled_at) }));
      } else if (data.duplicate_guard) {
        toast.info(platform === "facebook" ? t("Arrangementet er allerede publisert på Facebook nylig") : t("Arrangementet er allerede publisert på Instagram nylig"));
      } else {
        toast.success(platform === "facebook" ? t("Arrangementet er publisert på Facebook-siden!") : t("Arrangementet er publisert på Instagram!"));
      }
      await loadPosterSocial(posterTarget.id);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setPublishing(null);
    }
  };

  const cancelScheduled = async (jobId) => {
    if (!posterTarget) return;
    setCancellingJob(jobId);
    try {
      await api.delete(`/my/events/${posterTarget.id}/social/schedule/${jobId}`);
      toast.success(t("Den planlagte publiseringen er avbrutt"));
      await loadPosterSocial(posterTarget.id);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setCancellingJob(null);
    }
  };

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(posterSocial?.embed_html || "");
      toast.success(t("Innbyggingskoden er kopiert"));
    } catch {
      toast.error(t("Kunne ikke kopiere – marker teksten og kopier manuelt"));
    }
  };

  const fetchEvents = useCallback(() => {
    api
      .get("/my/events")
      .then((res) => setEvents(res.data))
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => setLoading(false));
    api
      .get("/my/sales")
      .then((res) => setSales(res.data))
      .catch(() => setSales(null));
  }, []);

  const [sendingWeeklyReport, setSendingWeeklyReport] = useState(false);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    api
      .get("/my/invoices")
      .then((res) => setInvoices(res.data.invoices || []))
      .catch(() => {});
  }, []);

  const downloadInvoice = async (inv) => {
    try {
      const res = await api.get(`/my/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `faktura-${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("Faktura {n} lastet ned", { n: inv.invoice_number }));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke laste ned fakturaen")));
    }
  };

  const sendWeeklyReport = async () => {
    setSendingWeeklyReport(true);
    try {
      const { data } = await api.post("/my/reports/weekly/send");
      toast.success(t(data.message || "Ukesrapporten er sendt til e-posten din"));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke sende ukesrapporten")));
    } finally {
      setSendingWeeklyReport(false);
    }
  };

  const exportSalesCsv = async () => {
    try {
      const res = await api.get("/my/sales/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "salgsrapport.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("Salgsrapporten er lastet ned"));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    // Defer chart mount one frame so the container has layout (avoids Recharts -1 size warning)
    const raf = requestAnimationFrame(() => setChartReady(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const togglePublish = async (event) => {
    try {
      const { data } = await api.patch(`/my/events/${event.id}/publish`, { published: !event.published });
      if (data?.published && data?.vipps_pending) {
        toast.info(t(data.message || "Publisert – publikum kan reservere og betale ved inngangen; Vipps-betaling åpner når Vipps er satt opp"), {
          duration: 9000,
          action: { label: t("Gå til Betaling"), onClick: () => navigate("/dashboard/betaling") },
        });
      } else {
        toast.success(!event.published ? t("Arrangementet er publisert") : t("Arrangementet er avpublisert"));
      }
      fetchEvents();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const duplicateEvent = async (event) => {
    try {
      const { data } = await api.post(`/my/events/${event.id}/duplicate`);
      toast.success(t("Kopi opprettet som kladd – sett ny dato og publiser"));
      navigate(`/dashboard/rediger/${data.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const deleteMatches =
    deleteTarget && deleteConfirmText.trim().toLowerCase() === (deleteTarget.title || "").trim().toLowerCase();

  const handleDelete = async () => {
    if (!deleteTarget || !deleteMatches) return;
    try {
      await api.delete(`/my/events/${deleteTarget.id}`);
      toast.success(t("Arrangementet er slettet"));
      setDeleteTarget(null);
      setDeleteConfirmText("");
      fetchEvents();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const totals = events.reduce(
    (acc, e) => ({
      issued: acc.issued + (e.issued || 0),
      checkedIn: acc.checkedIn + (e.checked_in || 0),
      capacity: acc.capacity + (e.is_external ? 0 : (e.capacity || 0)),
    }),
    { issued: 0, checkedIn: 0, capacity: 0 }
  );

  return (
    <DashboardLayout>
      <TooltipProvider delayDuration={150}>
      <div className="space-y-8">
        {organizer?.status === "pending" && (
          <div className="rounded-xl border border-border bg-[hsl(38,92%,95%)] text-[hsl(30,60%,25%)] px-4 py-3 text-sm" data-testid="pending-approval-banner">
            <span className="font-medium">{t("Kontoen venter på godkjenning.")}</span> {t("Vi fant ikke navnet ditt blant organisasjonens registrerte roller i Brønnøysundregistrene. Du kan lage utkast, men ikke publisere eller selge billetter før kontoen er godkjent.")}
          </div>
        )}
        {organizer?.status === "rejected" && (
          <div className="rounded-xl border border-border bg-[hsl(0,84%,96%)] text-[hsl(0,60%,30%)] px-4 py-3 text-sm" data-testid="rejected-banner">
            <span className="font-medium">{t("Kontoen er avvist")}</span> {t("og kan ikke publisere arrangementer. Kontakt Right in hvis du mener dette er feil.")}
          </div>
        )}
        {organizer?.email_verified === false && (
          <div className="rounded-xl border border-border bg-accent/60 px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center gap-3" data-testid="email-verify-banner">
            <p className="flex-1">
              <span className="font-medium">{t("Bekreft e-postadressen din.")}</span> {t("Vi har sendt en 6-sifret kode til {email}. Publisering låses opp når e-posten er bekreftet.", { email: organizer.email })}
            </p>
            <div className="flex gap-2 shrink-0">
              <Input
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder={t("6-sifret kode")}
                inputMode="numeric"
                className="w-32 h-9 bg-background font-mono tracking-widest"
                data-testid="email-verify-code-input"
              />
              <Button size="sm" onClick={submitVerifyCode} disabled={verifying} data-testid="email-verify-submit-button">
                {verifying ? t("Sjekker...") : t("Bekreft")}
              </Button>
              <Button size="sm" variant="ghost" onClick={resendVerifyCode} data-testid="email-verify-resend-button">
                {t("Send ny kode")}
              </Button>
            </div>
          </div>
        )}
        {organizer?.public_contact_complete === false && events.some((e) => e.is_paid) && (
          <div className="rounded-xl border border-border bg-accent/60 px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center gap-3" data-testid="contact-missing-banner">
            <p className="flex-1">
              <span className="font-medium">{t("Legg inn offentlig kontaktinfo.")}</span>{" "}
              {t("Vipps krever at org.nr., adresse, telefon og e-post er synlig der billettene selges. Det tar ett minutt – adressen kan hentes fra Brønnøysund.")}
            </p>
            <Button size="sm" asChild data-testid="contact-missing-banner-button">
              <Link to="/dashboard/kontaktinfo">{t("Fyll ut kontaktinfo")}</Link>
            </Button>
          </div>
        )}
        {/* Vipps ikke satt opp: salget er likevel åpent – publikum reserverer og betaler ved inngangen (regel fra eier) */}
        {events.some((e) => e.vipps_pending) && (
          <div className="rounded-xl border border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/15 px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center gap-3" data-testid="vipps-pending-banner">
            <p className="flex-1">
              <span className="font-medium">{t("Vipps er ikke satt opp for {what}.", { what: events.filter((e) => e.vipps_pending).length === 1 ? t("ett betalt arrangement") : t("{n} betalte arrangement", { n: events.filter((e) => e.vipps_pending).length }) })}</span>{" "}
              {t("Publikum kan reservere billetter og betale ved inngangen i mellomtiden – Vipps-betaling åpner automatisk når Vipps er satt opp.")}
            </p>
            <Button size="sm" asChild data-testid="vipps-pending-banner-button">
              <Link to="/dashboard/betaling">{t("Sett opp Vipps")}</Link>
            </Button>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="dashboard-title">
              {t("Hei, {name}", { name: organizer?.org_name })}
            </h1>
            <p className="text-muted-foreground mt-1">{t("Her er oversikten over arrangementene dine.")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={sendWeeklyReport}
              disabled={sendingWeeklyReport || events.length === 0}
              data-testid="dashboard-weekly-report-button"
            >
              <Mail className="w-4 h-4 mr-1.5" />
              {sendingWeeklyReport ? t("Sender...") : t("Send ukesrapport")}
            </Button>
            <Button asChild className="md:hidden" data-testid="dashboard-create-button">
              <Link to="/dashboard/ny">
                <Plus className="w-4 h-4 mr-1.5" />
                {t("Opprett arrangement")}
              </Link>
            </Button>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-brand-soft text-brand shrink-0"><CalendarDays className="w-4 h-4" /></span> {t("Arrangementer")}
              </div>
              <p className="text-2xl sm:text-3xl font-semibold tabular-nums" data-testid="metric-events">{events.length}</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-brand-soft text-brand shrink-0"><Banknote className="w-4 h-4" /></span> {t("Omsetning")}
              </div>
              <p className="text-2xl sm:text-3xl font-semibold tabular-nums" data-testid="metric-revenue">
                {(sales?.total_revenue ?? 0).toLocaleString("nb-NO")} kr
              </p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-brand-soft text-brand shrink-0"><Ticket className="w-4 h-4" /></span> {t("Utstedte billetter")}
              </div>
              <p className="text-2xl sm:text-3xl font-semibold tabular-nums" data-testid="metric-issued">{totals.issued}</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-brand-soft text-brand shrink-0"><CheckCircle2 className="w-4 h-4" /></span> {t("Sjekket inn")}
              </div>
              <p className="text-2xl sm:text-3xl font-semibold tabular-nums" data-testid="metric-checkedin">{totals.checkedIn}</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-brand-soft text-brand shrink-0"><Users className="w-4 h-4" /></span> {t("Total kapasitet")}
              </div>
              <p className="text-2xl sm:text-3xl font-semibold tabular-nums" data-testid="metric-capacity">{totals.capacity}</p>
            </CardContent>
          </Card>
        </div>

        {/* Admin: helse for svar-sporing (kontaktregisteret) */}
        {organizer?.is_admin && <ReplyTrackingHealthCard />}

        {/* Sales overview */}
        {sales && sales.tickets_sold > 0 && (
          <div className="space-y-4" data-testid="dashboard-sales-section">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-heading font-semibold text-lg">{t("Salgsoversikt")}</h2>
              <Button variant="secondary" size="sm" onClick={exportSalesCsv} data-testid="sales-export-csv-button">
                <Download className="w-4 h-4 mr-1.5" />{t("Salgsrapport (CSV)")}
              </Button>
            </div>
            <Card className="border-border">
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-heading font-semibold">
                      {salesPeriod === "week" ? t("Omsetning per uke") : t("Omsetning per måned")}
                    </h3>
                  </div>
                  <ToggleGroup
                    type="single"
                    size="sm"
                    value={salesPeriod}
                    onValueChange={(v) => v && setSalesPeriod(v)}
                    className="border border-border rounded-lg p-0.5"
                  >
                    <ToggleGroupItem value="week" className="px-3 text-xs" data-testid="sales-period-week">{t("Uke")}</ToggleGroupItem>
                    <ToggleGroupItem value="month" className="px-3 text-xs" data-testid="sales-period-month">{t("Måned")}</ToggleGroupItem>
                  </ToggleGroup>
                </div>
                <div className="h-56" data-testid="sales-chart">
                  {chartReady && (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={224} initialDimension={{ width: 600, height: 224 }}>
                    <BarChart data={(salesPeriod === "week" ? sales.by_week : sales.by_month) || []} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(214 20% 88%)" />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(215 16% 40%)" }} tickLine={false} axisLine={{ stroke: "hsl(214 20% 88%)" }} />
                      <YAxis tick={{ fontSize: 12, fill: "hsl(215 16% 40%)" }} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => `${v.toLocaleString("nb-NO")}`} />
                      <ChartTooltip
                        cursor={{ fill: "hsl(210 16% 94%)" }}
                        formatter={(value, name) =>
                          name === "Omsetning" ? [`${Number(value).toLocaleString("nb-NO")} kr`, t("Omsetning")] : [value, t("Billetter")]
                        }
                        contentStyle={{ borderRadius: 12, border: "1px solid hsl(214 20% 88%)", fontSize: 13 }}
                      />
                      <Bar dataKey="revenue" name="Omsetning" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} maxBarSize={48} />
                    </BarChart>
                  </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>
            <div className="grid lg:grid-cols-2 gap-4">
            <Card className="border-border">
              <CardContent className="p-0">
                <div className="flex items-center gap-2 px-5 pt-5 pb-3">
                  <Ticket className="w-4 h-4 text-muted-foreground" />
                  <h2 className="font-heading font-semibold">{t("Billettsalg per type")}</h2>
                </div>
                <Table data-testid="sales-by-type-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Billettype")}</TableHead>
                      <TableHead className="text-right">{t("Antall")}</TableHead>
                      <TableHead className="text-right">{t("Omsetning")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sales.by_type.map((row) => (
                      <TableRow key={row.ticket_type} data-testid={`sales-type-row-${row.ticket_type}`}>
                        <TableCell className="font-medium">{row.ticket_type}</TableCell>
                        <TableCell className="text-right tabular-nums">{row.quantity}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.revenue > 0 ? `${row.revenue.toLocaleString("nb-NO")} kr` : t("Gratis")}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-secondary/50 hover:bg-secondary/50" data-testid="sales-type-total-row">
                      <TableCell className="font-semibold">{t("Totalt")}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{sales.tickets_sold}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">
                        {sales.total_revenue.toLocaleString("nb-NO")} kr
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="p-0">
                <div className="flex items-center gap-2 px-5 pt-5 pb-3">
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                  <h2 className="font-heading font-semibold">{t("Salg per arrangement")}</h2>
                </div>
                <Table data-testid="sales-by-event-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Arrangement")}</TableHead>
                      <TableHead className="text-right">{t("Billetter")}</TableHead>
                      <TableHead className="text-right">{t("Omsetning")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sales.by_event.map((row) => (
                      <TableRow key={row.event_id} data-testid={`sales-event-row-${row.event_id}`}>
                        <TableCell>
                          <p className="font-medium truncate max-w-[220px]">{row.title}</p>
                          {row.date && <p className="text-xs text-muted-foreground">{formatDateTime(row.date)}</p>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{row.quantity}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.revenue > 0 ? `${row.revenue.toLocaleString("nb-NO")} kr` : t("Gratis")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            </div>
          </div>
        )}

        {/* Invoices */}
        {invoices.length > 0 && (
          <div className="space-y-4" data-testid="dashboard-invoices-section">
            <h2 className="font-heading font-semibold text-lg">{t("Fakturaer")}</h2>
            <Card className="border-border">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table data-testid="dashboard-invoices-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("Fakturanr.")}</TableHead>
                        <TableHead>{t("Arrangement")}</TableHead>
                        <TableHead className="text-right">{t("Beløp")}</TableHead>
                        <TableHead>{t("Forfall")}</TableHead>
                        <TableHead>{t("Status")}</TableHead>
                        <TableHead className="text-right">PDF</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((inv) => (
                        <TableRow key={inv.id} data-testid={`invoice-row-${inv.invoice_number}`}>
                          <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                          <TableCell className="text-sm">{inv.event_title}</TableCell>
                          <TableCell className="text-sm text-right tabular-nums font-medium">
                            {inv.total.toLocaleString("nb-NO", { minimumFractionDigits: 2 })} kr
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{(inv.due_date || "").slice(0, 10).split("-").reverse().join(".")}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{inv.status === "paid" ? t("Betalt") : t("Sendt")}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => downloadInvoice(inv)} data-testid={`invoice-download-${inv.invoice_number}`}>
                              <FileDown className="w-4 h-4 mr-1.5" />
                              {t("Last ned")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Events table */}
        <Card className="border-border">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-14 rounded-lg" />
                ))}
              </div>
            ) : events.length === 0 ? (
              <div className="p-12 text-center" data-testid="dashboard-empty-state">
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-secondary flex items-center justify-center">
                  <CalendarDays className="w-7 h-7 text-muted-foreground" />
                </div>
                <h3 className="font-heading font-semibold text-lg">{t("Ingen arrangementer ennå")}</h3>
                <p className="text-sm text-muted-foreground mt-1 mb-5">{t("Opprett ditt første arrangement og begynn å dele billetter.")}</p>
                <Button asChild data-testid="dashboard-empty-create-button">
                  <Link to="/dashboard/ny">
                    <Plus className="w-4 h-4 mr-1.5" />
                    {t("Opprett arrangement")}
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="dashboard-events-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Arrangement")}</TableHead>
                      <TableHead>{t("Dato")}</TableHead>
                      <TableHead>{t("Status")}</TableHead>
                      <TableHead className="text-right">{t("Billetter")}</TableHead>
                      <TableHead className="text-right">{t("Innsjekket")}</TableHead>
                      <TableHead className="text-right">{t("Visninger")}</TableHead>
                      <TableHead className="text-right">
                        <Tooltip>
                          <TooltipTrigger className="cursor-help underline decoration-dotted underline-offset-4">{t("Konv.")}</TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">{t("Konvertering: andel sidevisninger som ender i billett")}</TooltipContent>
                        </Tooltip>
                      </TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((event) => (
                      <TableRow key={event.id} data-testid={`dashboard-event-row-${event.id}`}>
                        <TableCell>
                          <p className="font-medium">{event.title}</p>
                          <p className="text-xs text-muted-foreground">{[event.venue, event.city].filter(Boolean).join(", ")}</p>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDateTime(event.date)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1">
                            {event.is_over ? (
                              <Badge variant="outline" className="text-muted-foreground" title={t("Datoen er passert – vises ikke lenger for publikum")} data-testid={`dashboard-event-over-${event.id}`}>{t("Avsluttet")}</Badge>
                            ) : event.published ? (
                              <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]">{t("Publisert")}</Badge>
                            ) : (
                              <Badge variant="secondary">{t("Kladd")}</Badge>
                            )}
                            {event.is_external && (
                              <Badge variant="outline" className="gap-1" title={t("Billetter/påmelding hos arrangøren – ikke via Right in")} data-testid={`dashboard-event-external-${event.id}`}>
                                <ExternalLink className="w-3 h-3" /> {event.external_host === "Facebook" ? "Facebook" : t("Ekstern")}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm">
                          {event.is_external ? <span className="text-muted-foreground" title={t("Billetter/påmelding hos arrangøren – ikke via Right in")}>–</span> : <>{event.issued} / {event.capacity}</>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{event.is_external ? <span className="text-muted-foreground">–</span> : event.checked_in}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm" data-testid={`event-views-${event.id}`}>
                          {event.views ?? 0}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm" data-testid={`event-conversion-${event.id}`}>
                          {event.is_external ? <span className="text-muted-foreground">–</span> : event.views > 0 ? `${Math.min(100, Math.round((event.issued / event.views) * 100))}%` : "–"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-0.5 flex-wrap min-w-[220px]">
                            <RowAction label={t("Rediger")} onClick={() => navigate(`/dashboard/rediger/${event.id}`)} testId={`event-action-edit-${event.id}`}>
                              <Pencil className="w-4 h-4" />
                            </RowAction>
                            {!event.is_external && (
                              <RowAction label={t("Deltakerliste")} onClick={() => navigate(`/dashboard/deltakere/${event.id}`)} testId={`event-action-attendees-${event.id}`}>
                                <Users className="w-4 h-4" />
                              </RowAction>
                            )}
                            {!event.is_external && (
                              <RowAction label={t("Sjekk inn")} onClick={() => navigate(`/dashboard/sjekk-inn/${event.id}`)} testId={`event-action-checkin-${event.id}`}>
                                <QrCode className="w-4 h-4" />
                              </RowAction>
                            )}
                            <RowAction label={t("Dupliser til ny dato")} onClick={() => duplicateEvent(event)} testId={`event-action-duplicate-${event.id}`}>
                              <Copy className="w-4 h-4" />
                            </RowAction>
                            {event.published && (
                              <RowAction label={t("Se offentlig side")} onClick={() => window.open(`/arrangement/${event.id}`, "_blank")} testId={`event-action-view-${event.id}`}>
                                <ExternalLink className="w-4 h-4" />
                              </RowAction>
                            )}
                            {event.published && (
                              <RowAction
                                label={t("QR-plakat (PDF)")}
                                onClick={() => {
                                  setPosterStyle("klassisk");
                                  setPosterFormat("A4");
                                  setPosterTarget(event);
                                }}
                                testId={`event-action-poster-${event.id}`}
                              >
                                <FileDown className="w-4 h-4" />
                              </RowAction>
                            )}
                            {event.published && (
                              <DropdownMenu>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                        aria-label={t("Del og publiser")}
                                        data-testid={`event-actions-share-${event.id}`}
                                      >
                                        <Share2 className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">{t("Del og promoter")}</TooltipContent>
                                </Tooltip>
                                <DropdownMenuContent align="end" className="w-60">
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        const { data } = await api.post(`/my/events/${event.id}/facebook-post`);
                                        toast.success(
                                          data.duplicate_guard
                                            ? t("Arrangementet er allerede publisert på Facebook nylig")
                                            : t("Arrangementet er publisert på Facebook-siden!")
                                        );
                                      } catch (err) {
                                        toast.error(errorMessage(err));
                                      }
                                    }}
                                    data-testid={`event-action-facebook-autopost-${event.id}`}
                                  >
                                    <Send className="w-4 h-4 mr-2" /> {t("Publiser til Facebook-side")}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openFacebookShare(eventShareUrl(event.id))} data-testid={`event-action-share-facebook-${event.id}`}>
                                    <Facebook className="w-4 h-4 mr-2" /> {t("Del på Facebook")}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await navigator.clipboard.writeText(eventShareUrl(event.id));
                                        toast.success(t("Delingslenken er kopiert – gir fin forhåndsvisning på sosiale medier"));
                                      } catch {
                                        toast.error(t("Kunne ikke kopiere lenken"));
                                      }
                                    }}
                                    data-testid={`event-action-share-copy-${event.id}`}
                                  >
                                    <Link2 className="w-4 h-4 mr-2" /> {t("Kopier delingslenke")}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            <RowAction
                              label={event.published ? t("Avpubliser") : t("Publiser")}
                              onClick={() => togglePublish(event)}
                              testId={`event-action-publish-${event.id}`}
                            >
                              {event.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </RowAction>
                            <RowAction label={t("Slett")} onClick={() => setDeleteTarget(event)} testId={`event-action-delete-${event.id}`} danger>
                              <Trash2 className="w-4 h-4" />
                            </RowAction>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteConfirmText("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Slette «{title}»?", { title: deleteTarget?.title })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("Dette sletter arrangementet og alle {n} utstedte billetter permanent. Handlingen kan ikke angres.", { n: deleteTarget?.issued || 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <p className="text-sm font-medium">
              {t("Skriv inn arrangementsnavnet for å bekrefte sletting:")}
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={deleteTarget?.title || ""}
              data-testid="delete-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">{t("Avbryt")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={!deleteMatches}
              className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:bg-[hsl(var(--danger))]/90 disabled:opacity-50 disabled:pointer-events-none"
              data-testid="delete-confirm-button"
            >
              {t("Slett arrangement")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* QR-plakat dialog */}
      <Dialog open={!!posterTarget} onOpenChange={(open) => !open && setPosterTarget(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="poster-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("QR-plakat og sosiale bilder")}</DialogTitle>
            <DialogDescription>
              {t("Slik blir plakaten for")}{" "}
              <span className="font-medium text-foreground">{posterTarget?.title}</span> {t("– velg stil og format, last ned eller publiser.")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-[240px,1fr] gap-5">
            {/* Ekte forhåndsvisning */}
            <div
              className={`relative rounded-lg border border-border overflow-hidden bg-secondary/40 sm:self-start ${
                posterIsPdf ? "aspect-[210/297]" : posterFormat === "square" ? "aspect-square" : posterFormat === "story" ? "aspect-[9/16] max-h-[430px] mx-auto" : "aspect-video"
              }`}
              data-testid="poster-preview-frame"
            >
              {posterPreview && (
                <img
                  src={posterPreview}
                  alt={t("Forhåndsvisning av plakaten")}
                  className="w-full h-full object-contain"
                  data-testid="poster-preview-image"
                />
              )}
              {posterPreviewLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-[2px]" data-testid="poster-preview-loading">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              )}
              {!posterPreview && !posterPreviewLoading && posterStyle === "ai" && !posterAi?.art && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground px-4 text-center" data-testid="poster-ai-empty">
                  {posterAiGenerating ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin text-brand" />
                      <span>{t("AI-en tegner plakaten … (opptil 1 minutt)")}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-6 h-6 text-brand" />
                      <span>
                        {posterAi && !posterAi.available
                          ? t("AI-design kan ikke brukes på dette arrangementet ennå – velg en annen stil, eller se forklaringen under «AI-design».")
                          : t("Ingen AI-forslag ennå. Trykk «Lag AI-design» for å få et unikt kunstverk til plakaten.")}
                      </span>
                    </>
                  )}
                </div>
              )}
              {!posterPreview && !posterPreviewLoading && !(posterStyle === "ai" && !posterAi?.art) && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground px-4 text-center">
                  {t("Kunne ikke lage forhåndsvisning – nedlastingen fungerer likevel")}
                </div>
              )}
              {posterAiGenerating && posterPreview && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/70 backdrop-blur-[2px] text-xs text-center px-4" data-testid="poster-ai-generating">
                  <Loader2 className="w-6 h-6 animate-spin text-brand" />
                  <span>{t("AI-en tegner plakaten … (opptil 1 minutt)")}</span>
                </div>
              )}
            </div>
            {/* Valg */}
            <div className="space-y-5">
              <div className="space-y-2">
                <p className="text-sm font-medium">{t("Stil")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { key: "klassisk", label: t("Klassisk"), dot: "bg-white border border-border" },
                    { key: "moerk", label: t("Mørk"), dot: "bg-[hsl(222,47%,11%)]" },
                    { key: "teal", label: t("Teal"), dot: "bg-[hsl(186,65%,29%)]" },
                    { key: "ai", label: t("AI-design"), icon: Sparkles, badge: t("Nytt") },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setPosterStyle(opt.key)}
                      aria-pressed={posterStyle === opt.key}
                      className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                        posterStyle === opt.key
                          ? "border-primary bg-secondary/60 text-foreground"
                          : "border-border text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground"
                      }`}
                      data-testid={`poster-style-${opt.key}`}
                    >
                      {opt.icon ? <opt.icon className="w-4 h-4 shrink-0 text-brand" /> : <span className={`w-4 h-4 rounded-full shrink-0 ${opt.dot}`} />}
                      {opt.label}
                      {opt.badge && <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-brand-soft text-brand">{opt.badge}</span>}
                      {posterStyle === opt.key && <Check className="w-4 h-4 ml-auto" />}
                    </button>
                  ))}
                </div>
              </div>
              {posterStyle === "ai" && (
                <div className="rounded-lg border border-brand/30 bg-brand-soft/30 p-3 space-y-2 text-sm" data-testid="poster-ai-panel">
                  {posterAiLoading && !posterAi ? (
                    <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {t("Sjekker AI-design …")}</p>
                  ) : !posterAi?.available ? (
                    <p className="text-muted-foreground" data-testid="poster-ai-unavailable">
                      {posterAi?.configured === false ? t("AI-design er ikke satt opp på serveren.") : t(posterAi?.reason || "AI-design er tilgjengelig for betalte arrangement, eller når Vipps er satt opp under Betaling")}
                    </p>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground">
                        {t("AI-en lager et unikt kunstverk ut fra tittel, kategori og omtale. Tekst, dato, pris og QR-kode legges alltid på av Right in – så alt er lesbart og skannbart.")}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" onClick={generatePosterAi} disabled={posterAiGenerating || posterAi.remaining <= 0} data-testid="poster-ai-generate-button">
                          {posterAiGenerating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : posterAi.art ? <RefreshCw className="w-4 h-4 mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                          {posterAiGenerating ? t("Lager …") : posterAi.art ? t("Lag nytt forslag") : t("Lag AI-design")}
                        </Button>
                        <span className="text-xs text-muted-foreground" data-testid="poster-ai-quota">
                          {posterAi.remaining > 0 ? t("{n} av {max} forslag igjen", { n: posterAi.remaining, max: posterAi.max }) : t("Alle {max} forslagene er brukt", { max: posterAi.max })}
                        </span>
                      </div>
                      {posterAi.last_error?.technical && (
                        <p className="text-xs text-muted-foreground break-words" data-testid="poster-ai-last-error">
                          <span className="font-medium text-foreground">{t("Siste feil")}{posterAi.last_error.at ? ` (${new Date(posterAi.last_error.at).toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Oslo" })})` : ""}:</span> {posterAi.last_error.technical}
                        </p>
                      )}
                      {posterAi.diagnostics && (posterAi.diagnostics.packages_ok === false || posterAi.diagnostics.key === "openai") && (
                        <p className="text-xs text-[hsl(var(--warning-foreground))] bg-[hsl(var(--warning))]/20 rounded-md px-2 py-1" data-testid="poster-ai-diagnostics">
                          {posterAi.diagnostics.packages_ok === false
                            ? t("Serveren mangler AI-pakkene: {err}", { err: posterAi.diagnostics.packages_error || "" })
                            : t("Serveren bruker en vanlig OpenAI-nøkkel, ikke Emergent-nøkkelen.")}
                        </p>
                      )}
                      {posterAi.history?.length > 1 && (
                        <div className="flex flex-wrap items-center gap-1.5" data-testid="poster-ai-history">
                          <span className="text-xs text-muted-foreground">{t("Forslag")}:</span>
                          {[...posterAi.history].reverse().map((h, i) => (
                            <button
                              key={h.id}
                              type="button"
                              onClick={() => !h.active && selectPosterArt(h.id)}
                              aria-pressed={h.active}
                              className={`rounded-md border px-2 py-1 text-xs transition-colors duration-150 ${h.active ? "border-primary bg-secondary/60 font-medium" : "border-border text-muted-foreground hover:text-foreground"}`}
                              data-testid={`poster-ai-art-${i + 1}`}
                            >
                              {i + 1}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {posterAi && (
                    <div className="border-t border-brand/20 pt-2 space-y-2" data-testid="poster-ai-selftest">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button variant="outline" size="sm" className="bg-background/70" onClick={runPosterAiTest} disabled={posterAiTesting} data-testid="poster-ai-selftest-button">
                          {posterAiTesting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Stethoscope className="w-4 h-4 mr-1.5" />}
                          {posterAiTesting ? t("Tester …") : t("Test AI-tjenesten")}
                        </Button>
                        <span className="text-xs text-muted-foreground">{t("Sjekker nøkkel og tilkobling – bruker ingen kreditt")}</span>
                      </div>
                      {posterAiTest && (
                        <ul className="space-y-1" data-testid="poster-ai-selftest-results">
                          {posterAiTest.steps.map((st) => {
                            const labels = {
                              packages: t("AI-pakker på serveren"),
                              key: st.kind === "emergent" ? t("Emergent-nøkkel funnet") : st.kind === "openai" ? t("OpenAI-nøkkel funnet") : t("AI-nøkkel"),
                              connection: t("Kontakt med AI-tjenesten"),
                              auth: t("Nøkkelen godtas av AI-tjenesten"),
                              model: t("Modellen {m} er tilgjengelig", { m: st.model || "gpt-image-1" }),
                            };
                            return (
                              <li key={st.id} className="flex items-start gap-2 text-xs" data-testid={`poster-ai-selftest-step-${st.id}`}>
                                {st.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[hsl(var(--success))]" /> : <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-destructive" />}
                                <span className="min-w-0">
                                  <span className={st.ok ? "" : "font-medium text-destructive"}>{labels[st.id] || st.id}</span>
                                  {st.id === "connection" && st.ok && typeof st.ms === "number" && <span className="text-muted-foreground"> · {st.ms} ms</span>}
                                  {st.detail && <span className="block text-muted-foreground break-words">{st.detail}</span>}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <p className="text-sm font-medium">{t("Format")}</p>
                <ToggleGroup
                  type="single"
                  size="sm"
                  value={posterFormat}
                  onValueChange={(v) => v && setPosterFormat(v)}
                  className="flex-wrap justify-start gap-1 border border-border rounded-lg p-0.5"
                  data-testid="poster-format-group"
                >
                  <ToggleGroupItem value="A4" className="px-3 text-xs" data-testid="poster-size-a4">A4</ToggleGroupItem>
                  <ToggleGroupItem value="A3" className="px-3 text-xs" data-testid="poster-size-a3">A3</ToggleGroupItem>
                  <ToggleGroupItem value="square" className="px-3 text-xs" data-testid="poster-format-square">{t("Kvadrat")}</ToggleGroupItem>
                  <ToggleGroupItem value="story" className="px-3 text-xs" data-testid="poster-format-story">{t("Story")}</ToggleGroupItem>
                  <ToggleGroupItem value="landscape" className="px-3 text-xs" data-testid="poster-format-landscape">16:9</ToggleGroupItem>
                </ToggleGroup>
                <p className="text-xs text-muted-foreground" data-testid="poster-format-hint">
                  {posterIsPdf
                    ? (posterStyle === "ai"
                      ? t("Arrangementsbildet ditt vises som et innfelt bilde oppå AI-kunsten, og QR-koden tar publikum rett til billettkjøp.")
                      : t("Hele arrangementsbildet vises med riktige proporsjoner, og QR-koden tar publikum rett til billettkjøp."))
                    : posterFormat === "square"
                      ? t("Kvadrat 1080×1080 – passer Facebook- og Instagram-innlegg. Samme kunst og QR-kode som plakaten.")
                      : posterFormat === "story"
                        ? t("Story 1080×1920 – for Instagram- og Facebook-story. Tekst og QR ligger innenfor det trygge feltet.")
                        : t("16:9 1200×675 – brukes som arrangementsbilde på Right in og i delingsforhåndsvisninger.")}
                </p>
              </div>

              {/* Publiser: heimeside / Facebook / Instagram */}
              <div className="rounded-lg border border-border p-3 space-y-2.5" data-testid="poster-publish-panel">
                <div>
                  <p className="text-sm font-medium">{t("Publiser")}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("Bruker stilen «{style}». Hjemmeside bruker 16:9-bildet; Facebook og Instagram bruker kvadratet.", {
                      style: { klassisk: t("Klassisk"), moerk: t("Mørk"), teal: t("Teal"), ai: t("AI-design") }[posterStyle],
                    })}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={publishHomepage}
                    disabled={!!publishing || (posterStyle === "ai" && !posterAi?.art)}
                    data-testid="poster-publish-homepage-button"
                  >
                    {publishing === "homepage" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Globe className="w-4 h-4 mr-1.5" />}
                    {t("Publiser til hjemmeside")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openConfirm("facebook")}
                    disabled={!!publishing || !posterSocial?.facebook?.configured || (posterStyle === "ai" && !posterAi?.art)}
                    data-testid="poster-publish-facebook-button"
                  >
                    {publishing === "facebook" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Facebook className="w-4 h-4 mr-1.5" />}
                    {t("Publiser til Facebook")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openConfirm("instagram")}
                    disabled={!!publishing || !posterSocial?.instagram?.configured || (posterStyle === "ai" && !posterAi?.art)}
                    data-testid="poster-publish-instagram-button"
                  >
                    {publishing === "instagram" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Instagram className="w-4 h-4 mr-1.5" />}
                    {t("Publiser til Instagram")}
                  </Button>
                </div>
                <ul className="space-y-1 text-xs text-muted-foreground" data-testid="poster-publish-status">
                  <li className="flex items-start gap-2" data-testid="poster-publish-status-homepage">
                    <Globe className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    {posterSocial?.homepage?.active ? (
                      <>
                        <span className="text-foreground">
                          {t("På Right in siden {date} ({style})", {
                            date: formatDateTime(posterSocial.homepage.created_at),
                            style: { klassisk: t("Klassisk"), moerk: t("Mørk"), teal: t("Teal"), ai: t("AI-design") }[posterSocial.homepage.style] || posterSocial.homepage.style,
                          })}
                        </span>
                        <button type="button" className="underline underline-offset-2 hover:text-foreground" onClick={() => setEmbedOpen(true)} data-testid="poster-embed-button">
                          {t("Innbyggingskode")}
                        </button>
                        <button type="button" className="underline underline-offset-2 hover:text-foreground disabled:opacity-50" onClick={revertHomepage} disabled={!!publishing} data-testid="poster-revert-homepage-button">
                          {publishing === "revert" ? t("Tilbakestiller …") : t("Bruk eget bilde igjen")}
                        </button>
                      </>
                    ) : (
                      <span>{t("Hjemmeside: bytter arrangementsbildet på Right in til plakatbildet, og gir deg innbyggingskode til egen nettside.")}</span>
                    )}
                    </div>
                  </li>
                  <li className="flex items-start gap-2" data-testid="poster-publish-status-facebook">
                    <Facebook className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    {!posterSocial?.facebook?.configured ? (
                      <span>{t("Facebook-publisering er ikke satt opp på serveren ennå.")}</span>
                    ) : posterSocial.facebook.created_at ? (
                      <>
                        <span className="text-foreground">{t("Publisert på Facebook {date}", { date: formatDateTime(posterSocial.facebook.created_at) })}</span>
                        {posterSocial.facebook.url && (
                          <a href={posterSocial.facebook.url} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-foreground" data-testid="poster-facebook-post-link">
                            {t("Se innlegget")}
                          </a>
                        )}
                      </>
                    ) : (
                      <span>{t("Legger ut bilde og tekst på Right in sin Facebook-side.")}</span>
                    )}
                    </div>
                  </li>
                  <li className="flex items-start gap-2" data-testid="poster-publish-status-instagram">
                    <Instagram className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    {!posterSocial?.instagram?.configured ? (
                      <span>{posterSocial?.facebook?.configured && posterSocial?.instagram?.reason ? t(posterSocial.instagram.reason) : t("Instagram-publisering er ikke satt opp på serveren ennå.")}</span>
                    ) : posterSocial.instagram.created_at ? (
                      <>
                        <span className="text-foreground">{t("Publisert på Instagram {date}", { date: formatDateTime(posterSocial.instagram.created_at) })}</span>
                        {posterSocial.instagram.permalink && (
                          <a href={posterSocial.instagram.permalink} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-foreground" data-testid="poster-instagram-post-link">
                            {t("Se innlegget")}
                          </a>
                        )}
                      </>
                    ) : (
                      <span>{t("Legger ut bilde og tekst på Right in sin Instagram-konto{username}.", { username: posterSocial.instagram.username ? ` (@${posterSocial.instagram.username})` : "" })}</span>
                    )}
                    {posterSocial?.instagram_story?.created_at && (
                      <span className="text-foreground" data-testid="poster-instagram-story-status">
                        · {t("Story publisert {date}", { date: formatDateTime(posterSocial.instagram_story.created_at) })}
                      </span>
                    )}
                    </div>
                  </li>
                </ul>
                {posterSocial?.scheduled?.length > 0 && (
                  <ul className="space-y-1 text-xs border-t border-border pt-2" data-testid="poster-schedule-list">
                    {posterSocial.scheduled.map((job) => (
                      <li key={job.id} className="flex items-start gap-2" data-testid={`poster-schedule-${job.id}`}>
                        <CalendarDays className="w-3.5 h-3.5 shrink-0 mt-0.5 text-muted-foreground" />
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className={job.status === "failed" ? "text-destructive" : job.status === "cancelled" ? "text-muted-foreground line-through" : "text-foreground"}>
                            {{ scheduled: t("Planlagt"), sending: t("Publiserer …"), sent: t("Publisert (planlagt)"), failed: t("Feilet"), cancelled: t("Avbrutt") }[job.status] || job.status}
                            {": "}
                            {job.platform === "instagram" ? "Instagram" : "Facebook"}
                            {job.platform === "instagram" && job.kind !== "post" ? ` (${job.kind === "story" ? t("story") : t("innlegg + story")})` : ""}
                            {" · "}
                            {formatDateTime(job.scheduled_at)}
                            {" · "}
                            {{ klassisk: t("Klassisk"), moerk: t("Mørk"), teal: t("Teal"), ai: t("AI-design") }[job.style] || job.style}
                          </span>
                          {job.status === "failed" && job.error && <span className="text-muted-foreground">– {t(job.error)}</span>}
                          {job.status === "sent" && (job.result?.url || job.result?.permalink) && (
                            <a href={job.result.url || job.result.permalink} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-foreground" data-testid={`poster-schedule-link-${job.id}`}>
                              {t("Se innlegget")}
                            </a>
                          )}
                          {job.status === "scheduled" && (
                            <button type="button" className="underline underline-offset-2 hover:text-foreground disabled:opacity-50" onClick={() => cancelScheduled(job.id)} disabled={cancellingJob === job.id} data-testid={`poster-schedule-cancel-${job.id}`}>
                              {cancellingJob === job.id ? t("Avbryter …") : t("Avbryt")}
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPosterTarget(null)} data-testid="poster-cancel-button">
              {t("Avbryt")}
            </Button>
            <Button onClick={downloadPoster} disabled={posterDownloading || (posterStyle === "ai" && !posterAi?.art)} data-testid="poster-download-button">
              <FileDown className="w-4 h-4 mr-1.5" />
              {posterDownloading
                ? t("Lager …")
                : posterIsPdf
                  ? t("Last ned {size}-plakat", { size: posterFormat })
                  : t("Last ned {format} (JPG)", { format: { square: t("kvadrat"), story: t("story"), landscape: "16:9" }[posterFormat] })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bekreft publisering til Facebook / Instagram */}
      <AlertDialog open={!!confirmPublish} onOpenChange={(open) => !open && setConfirmPublish(null)}>
        <AlertDialogContent data-testid="poster-publish-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              {confirmPublish === "instagram" ? t("Publiser på Right in sin Instagram-konto?") : t("Publiser på Right in sin Facebook-side?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("Kvadratbildet i stilen «{style}» og en tekst med tittel, dato, sted og lenke til billettsiden legges ut offentlig. Innlegget kan ikke angres herfra – det må slettes i {platform}.", {
                style: { klassisk: t("Klassisk"), moerk: t("Mørk"), teal: t("Teal"), ai: t("AI-design") }[posterStyle],
                platform: confirmPublish === "instagram" ? "Instagram" : "Facebook",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            {confirmPublish === "instagram" && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{t("Hva skal publiseres?")}</p>
                <ToggleGroup type="single" size="sm" value={igKind} onValueChange={(v) => v && setIgKind(v)} className="flex-wrap justify-start gap-1 border border-border rounded-lg p-0.5" data-testid="poster-ig-kind-group">
                  <ToggleGroupItem value="post" className="px-3 text-xs" data-testid="poster-ig-kind-post">{t("Innlegg (kvadrat)")}</ToggleGroupItem>
                  <ToggleGroupItem value="story" className="px-3 text-xs" data-testid="poster-ig-kind-story">{t("Story (9:16)")}</ToggleGroupItem>
                  <ToggleGroupItem value="both" className="px-3 text-xs" data-testid="poster-ig-kind-both">{t("Begge")}</ToggleGroupItem>
                </ToggleGroup>
                <p className="text-xs text-muted-foreground">{t("Storyen bruker story-bildet (1080×1920) uten tekst i innlegget, og forsvinner etter 24 timer.")}</p>
              </div>
            )}
            <div className="space-y-2">
              <p className="text-sm font-medium">{t("Når?")}</p>
              <ToggleGroup type="single" size="sm" value={publishWhen} onValueChange={(v) => v && setPublishWhen(v)} className="justify-start gap-1 border border-border rounded-lg p-0.5" data-testid="poster-publish-when-group">
                <ToggleGroupItem value="now" className="px-3 text-xs" data-testid="poster-publish-when-now">{t("Publiser nå")}</ToggleGroupItem>
                <ToggleGroupItem value="schedule" className="px-3 text-xs" data-testid="poster-publish-when-schedule">{t("Planlegg")}</ToggleGroupItem>
              </ToggleGroup>
              {publishWhen === "schedule" && (
                <div className="space-y-1">
                  <Input
                    type="datetime-local"
                    value={scheduleAt}
                    onChange={(e) => setScheduleAt(e.target.value)}
                    className="h-10 w-full sm:w-64"
                    data-testid="poster-publish-schedule-input"
                  />
                  <p className="text-xs text-muted-foreground">{t("Publiseres automatisk på dette tidspunktet (norsk tid). Du kan avbryte frem til det går ut.")}</p>
                </div>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="poster-publish-confirm-cancel">{t("Avbryt")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                if (publishWhen === "schedule" && !scheduleAt) {
                  e.preventDefault();
                  toast.error(t("Velg dato og klokkeslett for publiseringen"));
                  return;
                }
                publishSocial(confirmPublish);
              }}
              data-testid="poster-publish-confirm-button"
            >
              {publishWhen === "schedule" ? <CalendarDays className="w-4 h-4 mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />} {publishWhen === "schedule" ? t("Planlegg publisering") : t("Publiser")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Innbyggingskode til egen nettside */}
      <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="poster-embed-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("Innbyggingskode til egen nettside")}</DialogTitle>
            <DialogDescription>
              {t("Lim inn denne HTML-koden på nettsiden din. Bildet lenker til billettsiden på Right in og oppdateres når du publiserer på nytt.")}
            </DialogDescription>
          </DialogHeader>
          <Textarea readOnly value={posterSocial?.embed_html || ""} rows={6} className="font-mono text-xs" onFocus={(e) => e.target.select()} data-testid="poster-embed-code" />
          {posterSocial?.images?.square?.public_url && (
            <p className="text-xs text-muted-foreground break-all">
              {t("Direkte bildelenke")}:{" "}
              <a href={posterSocial.images.square.public_url} target="_blank" rel="noreferrer" className="underline underline-offset-2" data-testid="poster-embed-image-link">
                {posterSocial.images.square.public_url}
              </a>
            </p>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEmbedOpen(false)} data-testid="poster-embed-close-button">{t("Lukk")}</Button>
            <Button onClick={copyEmbed} data-testid="poster-embed-copy-button">
              <Copy className="w-4 h-4 mr-1.5" /> {t("Kopier koden")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </TooltipProvider>
    </DashboardLayout>
  );
}
